/**
 * Numbered loader displayed in the bottom-right corner during page
 * transitions. Counts from 000 to 100 in the Dahlia font, each digit
 * rolling in its own column like an odometer wheel (tabular columns, so
 * nothing shifts).
 *
 * ## Motion
 * Nothing here animates from main-thread JS frames. The value's path is
 * baked ahead of time as samples (counterMotion.ts) and each wheel's
 * positions along it are played by the compositor as a Web Animation
 * (odometerKeyframes), so the digits keep rolling while the next page's
 * mount stalls the main thread. A new target rebuilds the path from the
 * state the value is in at that instant: no snap, and the speed carries
 * over. Animations are anchored to the clock time the path was built at,
 * so a late first frame starts mid-way rather than late.
 *
 * ## Two ways to feed it
 * - **Simulated** (route changes): the digits follow an exponential
 *   approach to 100 that never arrives (`counter.simulatedTauMs`); when
 *   `complete` becomes true they run on to exactly 100 and fire
 *   `onCountComplete`. Given `finishAtMs`, that run is timed to land
 *   then: the moment the transition's logo comes to rest, so the two
 *   finish together. It arrives a beat after `complete` (the logo's
 *   worker answers), and the run is re-planned from where the digits
 *   are, at the speed they have, so nothing snaps.
 * - **Real** (`progress` 0 → 1, the portfolio's first-load entrance):
 *   the digits follow the real value through two cascaded lags
 *   (`entrance.progress.smoothingMs`); progress 1 is the same run to 100
 *   and `onCountComplete`.
 * In both, the digits never roll faster than `counter.maxUnitsPerS`, so
 * a flood of bytes is a steady climb, and the finishing run starts at
 * the speed they have and ends at a standstill (`counter.sprint`).
 *
 * ## Pace (`landAt`)
 * Real mode can be given a time to land on 100 by, if loading allows:
 * the entrance's ring takes a fixed time to deal, and on a fast
 * connection the bytes are in long before. The digits then climb
 * straight from where they are towards 100 (`paceCurve`), never ahead
 * of the real value, timed so the finishing run lands at that moment.
 * On a slow connection the real value is the lower of the two and
 * nothing changes.
 *
 * Stays showing "100" until the parent fades it out (`fading`) and
 * eventually unmounts it.
 *
 * ## Positioning
 * By default absolute in the bottom-right corner at z-35 (above the logo
 * overlay z-30). `className` replaces that placement when the counter is
 * laid out by a parent (the entrance's loader cluster).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { animationConfig } from "../../config/animation";
import { odometerKeyframes } from "./odometer";
import {
    AT_REST,
    follow,
    paceCurve,
    sprint,
    stateAt,
    trajectoryDurationMs,
    type Trajectory,
} from "./counterMotion";

/** Loader fade-out timing, shared with the logo-overlay fade in
 *  PageTransition.tsx so both fades happen on the same shape and duration.
 *  Sourced from animationConfig.pageTransition.loaderFadeOut. */
const LF = animationConfig.pageTransition.loaderFadeOut;
const LOADER_FADE_TRANSITION = `opacity ${LF.durationS}s ${LF.easing}`;

const COUNTER = animationConfig.pageTransition.counter;
const DT = COUNTER.sampleMs;
/** The follower's speed cap (units per ms) and how fast it may get
 *  there (units per ms²). */
const MAX_SPEED = COUNTER.maxUnitsPerS / 1000;
const MAX_ACCEL = MAX_SPEED / COUNTER.rampMs;

/** Real-progress lag (ms per stage): the entrance's. */
const REAL_LAG_MS = animationConfig.portfolio.entrance.progress.smoothingMs;

/** How far ahead a follower is baked (ms). A step settles well within
 *  this; the simulated curve runs on to the ready timeout, where the
 *  transition forces the sprint anyway. */
const REAL_HORIZON_MS = 4000;
const SIMULATED_HORIZON_MS = animationConfig.pageTransition.readyTimeoutMs + 2000;

/** Real progress arrives per network chunk; rebuilding three compositor
 *  animations for every one is wasted work, so rebuilds are spaced out
 *  and the latest target wins. */
const MIN_REBUILD_GAP_MS = 50;

interface NumberedLoaderProps {
    /** Simulated mode: when true, the counter finishes to 100. */
    complete?: boolean;
    /** Simulated mode: land on 100 at this time (performance.now() ms)
     *  rather than at the finishing run's own pace. May arrive, or move,
     *  after `complete`; the run is re-planned from where the digits are. */
    finishAtMs?: number;
    /** Real mode: progress 0 → 1. When given, `complete` is ignored. */
    progress?: number;
    /** Real mode: land on 100 at `atMs` (performance.now() ms) if loading
     *  allows, climbing from where the digits are at `startMs`. */
    landAt?: { startMs: number; atMs: number };
    /** Text color (should contrast with the curtain background). Omit
     *  when the parent sets the colour (e.g. a blend-mode class). */
    color?: string;
    /** When true, fade out via CSS transition */
    fading?: boolean;
    /** Fires when the counter reaches 100 */
    onCountComplete?: () => void;
    /** Placement + colour classes; defaults to the bottom-right corner. */
    className?: string;
}

const DEFAULT_PLACEMENT = "absolute bottom-[var(--bleed)] right-0 p-[3rem] z-[35]";

const WHEEL_DIGITS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 0];

/** A trajectory that ends on 100: nothing follows it. `landAtMs` is the
 *  time it was asked to land at, if any, so a new one rebuilds it. */
type CounterTrajectory = Trajectory & { final: boolean; landAtMs: number | null };

/** "Now" for the model and the animations' start times: the real clock,
 *  not `document.timeline.currentTime`. The timeline only advances when
 *  the main thread renders a frame, so during a stall it falls behind
 *  the compositor, which keeps drawing the wheels on real time; a path
 *  rebuilt from that stale time would start from where the wheels had
 *  been and jump them back. The two clocks share an origin, so a real
 *  time is a valid start time. */
const timelineNow = () => performance.now();

function Wheel({ innerRef }: { innerRef: (el: HTMLSpanElement | null) => void }) {
    return (
        <span className="inline-block h-[1em] overflow-hidden align-top">
            <span ref={innerRef} className="block will-change-transform">
                {WHEEL_DIGITS.map((d, i) => (
                    // Centred: the column is as wide as its widest digit, so
                    // a narrow "1" sits in the middle of its wheel like a
                    // tabular figure would.
                    <span key={i} className="block h-[1em] leading-none text-center">
                        {d}
                    </span>
                ))}
            </span>
        </span>
    );
}

export default function NumberedLoader({
    complete = false,
    finishAtMs,
    progress,
    landAt,
    color,
    fading = false,
    onCountComplete,
    className,
}: NumberedLoaderProps) {
    /** The value the digits are heading for, for the accessible label. */
    const [target, setTarget] = useState(0);

    const wheelRefs = useRef<(HTMLSpanElement | null)[]>([null, null, null]);
    const animsRef = useRef<Animation[]>([]);
    const trajRef = useRef<CounterTrajectory | null>(null);
    /** Highest target followed so far: progress never rolls back. */
    const targetRef = useRef(0);
    /** The simulated curve's zero: the moment the counter appeared. */
    const mountMsRef = useRef<number | null>(null);
    const lastBuildMsRef = useRef(-Infinity);
    /** The pace set from `landAt` (see the file comment), keyed by its
     *  times so a re-render with the same times keeps it. */
    const paceRef = useRef<{ key: string; at: (tMs: number) => number; endMs: number } | null>(null);
    /** The pace the current path was built on. */
    const builtPaceKeyRef = useRef("");
    /** Bumped when the pace reaches 100 with loading already done: the
     *  finishing run starts then, not before. */
    const [paceTick, bumpPaceTick] = useState(0);

    // Stable ref for the callback the completion timer fires
    const onCountCompleteRef = useRef(onCountComplete);
    onCountCompleteRef.current = onCountComplete;
    const realMode = progress !== undefined;

    /** Hands a trajectory to the compositor: one linear keyframe animation
     *  per wheel, anchored at the trajectory's start time. */
    const play = useCallback((traj: Trajectory) => {
        animsRef.current.forEach((a) => a.cancel());
        animsRef.current = [];
        const duration = trajectoryDurationMs(traj);
        wheelRefs.current.forEach((el, wheel) => {
            if (!el) return;
            const frames = odometerKeyframes(traj.values, wheel as 0 | 1 | 2).map(({ offset, position }) => ({
                offset,
                transform: `translate3d(0, ${(-position).toFixed(4)}em, 0)`,
            }));
            // "both": the start time can be a few ms ahead of the main
            // thread's timeline (see timelineNow), and the first keyframe
            // holds until then instead of the wheel showing its rest pose.
            const anim = el.animate(frames, { duration, easing: "linear", fill: "both" });
            anim.startTime = traj.startMs;
            animsRef.current.push(anim);
        });
    }, []);

    // Mount / unmount. On a remount (StrictMode) the current trajectory is
    // played again from its own start time, so it carries on where it is.
    useEffect(() => {
        if (trajRef.current) play(trajRef.current);
        return () => {
            animsRef.current.forEach((a) => a.cancel());
            animsRef.current = [];
        };
    }, [play]);

    // ── Counting ─────────────────────────────────────────────────────
    // Each change of input rebuilds the path from the value's state now.
    const landStartMs = landAt?.startMs;
    const landAtMs = landAt?.atMs;
    useEffect(() => {
        // The pace, set once per `landAt`, from where the digits were as
        // the ring started dealing.
        const paceKey = realMode && landStartMs !== undefined && landAtMs !== undefined ? `${landStartMs}:${landAtMs}` : "";
        if (paceKey && paceRef.current?.key !== paceKey) {
            const v0 = trajRef.current ? stateAt(trajRef.current, landStartMs!).value : 0;
            const { at, endMs } = paceCurve(v0, landStartMs!, landAtMs!, REAL_LAG_MS, COUNTER.sprint);
            paceRef.current = { key: paceKey, at, endMs };
        }
        const pace = paceKey ? paceRef.current : null;

        const finishing = realMode
            ? (progress ?? 0) >= 1 && (!pace || timelineNow() >= pace.endMs)
            : complete;

        if (finishing) {
            let traj = trajRef.current;
            const now = timelineNow();
            const landAtMs = realMode ? null : finishAtMs ?? null;
            // A new landing time re-plans the run, unless the digits have
            // already landed: 100 is 100.
            const landed = traj?.final && traj.startMs + trajectoryDurationMs(traj) <= now;
            if (!traj?.final || (traj.landAtMs !== landAtMs && !landed)) {
                const from = traj ? stateAt(traj, now) : AT_REST;
                const landInMs = landAtMs === null ? undefined : landAtMs - now;
                const values = sprint(from.value, from.speed, 100, DT, COUNTER.sprint, landInMs);
                traj = { startMs: now, dtMs: DT, values, inner: values, final: true, landAtMs };
                trajRef.current = traj;
                play(traj);
                setTarget(100);
            }
            // Fires when the wheels land. A timer, not the animation's
            // `finished` promise: a remount replays the animation and
            // would lose the promise.
            const id = window.setTimeout(
                () => onCountCompleteRef.current?.(),
                Math.max(0, traj.startMs + trajectoryDurationMs(traj) - timelineNow()),
            );
            return () => window.clearTimeout(id);
        }

        const build = () => {
            const now = timelineNow();
            if (mountMsRef.current === null) mountMsRef.current = now;
            const from = trajRef.current ? stateAt(trajRef.current, now) : AT_REST;
            let path: Pick<Trajectory, "values" | "inner">;
            if (realMode) {
                const next = Math.max(targetRef.current, (progress ?? 0) * 100);
                const paceChanged = (pace?.key ?? "") !== builtPaceKeyRef.current;
                // Same target and pace: the path already on the compositor is right.
                if (trajRef.current && next - targetRef.current < 0.05 && !paceChanged) return;
                targetRef.current = next;
                builtPaceKeyRef.current = pace?.key ?? "";
                // Never ahead of the real value; never ahead of the pace.
                const input = pace ? (t: number) => Math.min(next, pace.at(now + t)) : () => next;
                path = follow(from, input, REAL_LAG_MS, DT, REAL_HORIZON_MS, MAX_SPEED, MAX_ACCEL);
            } else {
                // The simulated curve has no inputs after mount: a re-run
                // of this effect (a StrictMode remount) changes nothing.
                if (trajRef.current) return;
                const since = now - mountMsRef.current;
                const tau = COUNTER.simulatedTauMs;
                path = follow(
                    from,
                    (t) => 100 * (1 - Math.exp(-(since + t) / tau)),
                    COUNTER.lagMs,
                    DT,
                    SIMULATED_HORIZON_MS,
                    MAX_SPEED,
                    MAX_ACCEL,
                );
            }
            trajRef.current = { startMs: now, dtMs: DT, ...path, final: false, landAtMs: null };
            lastBuildMsRef.current = now;
            play(trajRef.current);
            setTarget(path.values[path.values.length - 1]);
        };

        // Loading done before the pace reaches 100: the finish starts
        // when it does.
        let finishTimer: number | undefined;
        const armFinish = () => {
            if (!pace || targetRef.current < 100) return;
            const until = pace.endMs - timelineNow();
            if (until > 0) finishTimer = window.setTimeout(() => bumpPaceTick((n) => n + 1), until + 1);
        };

        const wait = lastBuildMsRef.current + MIN_REBUILD_GAP_MS - timelineNow();
        if (wait <= 0) {
            build();
            armFinish();
            return () => window.clearTimeout(finishTimer);
        }
        const id = window.setTimeout(() => {
            build();
            armFinish();
        }, wait);
        return () => {
            window.clearTimeout(id);
            window.clearTimeout(finishTimer);
        };
    }, [complete, finishAtMs, progress, realMode, landStartMs, landAtMs, paceTick, play]);

    // ── Render ────────────────────────────────────────────────────────

    const label = String(Math.round(Math.max(0, Math.min(100, target)))).padStart(3, "0");

    return (
        <div
            className={`
                ${className ?? DEFAULT_PLACEMENT}
                pointer-events-none select-none
                ${fading ? "opacity-0" : "opacity-100"}
            `}
            style={{ color, transition: LOADER_FADE_TRANSITION }}
            role="status"
            aria-live="off"
            aria-label={`Loading ${label}`}
        >
            <span
                className="font-dahlia font-medium text-[clamp(56px,9vw,128px)] leading-none tabular-nums whitespace-nowrap"
                aria-hidden="true"
            >
                {[0, 1, 2].map((wheel) => (
                    <Wheel key={wheel} innerRef={(el) => { wheelRefs.current[wheel] = el; }} />
                ))}
            </span>
        </div>
    );
}
